import { all, select, takeEvery, fork, put, call } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'

// action
import { userActions } from '@redux/store/index'

// lib
import { Logger } from '@utils'

// api
import { authApi, ResType } from '@api'

// type
import { UserType } from '@schemas'
import { User } from '@schemas'

function* userSaga() {
	try {
		const userLanguage: string = yield select(state => state.user.language)

		if (userLanguage === '') {
			const user: UserType = yield select(state => state.user.user)
			const newUserLanguage: string = user.language === '' ? 'KO' : user.language
			yield put(userActions.LANGUAGE(newUserLanguage))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

// ── 관리자 My Page ──────────────────────────────────────────────────────────
// 전부 `/auth/*` 의 self 엔드포인트를 쓴다 — 경로에 사용자 ID 를 넘기지 않으므로
// 남의 계정을 건드릴 수 없다. (관리자 그리드의 `/users/:id/...` 와 다른 경로다)

const apiUpdateSelfProfile = async (body: {
	name: string
	language: string
}): Promise<ResType | undefined> => await authApi.updateSelfProfile({ body: body })

const apiUpdateSelfPassword = async (body: {
	prevPassword: string
	newPassword: string
}): Promise<ResType | undefined> => await authApi.updateSelfPassword({ body: body })

const apiUpdateSelfProfileImage = async (file: File): Promise<ResType | undefined> =>
	await authApi.updateSelfProfileImage({ files: [file] })

/** 서버 상태코드를 사람이 읽을 문장으로. 사유가 다르면 문장도 달라야 한다 */
const selfErrorMessage = (apiRes: ResType | undefined, fallback: string): string => {
	const status: number = apiRes?.status ?? 0

	if (status === 400) return '현재 비밀번호가 맞지 않습니다.'
	if (status === 404) return '계정을 찾을 수 없습니다.'
	if (status === 401) return '다시 로그인해 주세요.'

	return fallback
}

function* updateSelfProfileSaga(
	action: PayloadAction<{ name: string; language: string }>,
) {
	try {
		yield put(userActions.SELF_STATUS('saving'))
		yield put(userActions.SELF_ERROR_MESSAGE(''))

		const apiRes: ResType = yield call(apiUpdateSelfProfile, action.payload)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			const data = apiRes.data[0] as any
			yield put(userActions.USER(new User(data)))
			yield put(userActions.SELF_STATUS('saved'))
		} else {
			yield put(userActions.SELF_STATUS('idle'))
			yield put(
				userActions.SELF_ERROR_MESSAGE(
					selfErrorMessage(apiRes, '저장하지 못했습니다. 다시 시도해 주세요.'),
				),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(userActions.SELF_STATUS('idle'))
		yield put(userActions.SELF_ERROR_MESSAGE('저장하지 못했습니다.'))
	}
}

function* updateSelfPasswordSaga(
	action: PayloadAction<{ prevPassword: string; newPassword: string }>,
) {
	try {
		yield put(userActions.SELF_STATUS('saving'))
		yield put(userActions.SELF_ERROR_MESSAGE(''))

		const apiRes: ResType = yield call(apiUpdateSelfPassword, action.payload)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			yield put(userActions.SELF_STATUS('saved'))
		} else {
			yield put(userActions.SELF_STATUS('idle'))
			yield put(
				userActions.SELF_ERROR_MESSAGE(
					selfErrorMessage(apiRes, '비밀번호를 바꾸지 못했습니다.'),
				),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(userActions.SELF_STATUS('idle'))
		yield put(userActions.SELF_ERROR_MESSAGE('비밀번호를 바꾸지 못했습니다.'))
	}
}

function* updateSelfProfileImageSaga(action: PayloadAction<{ file: File }>) {
	try {
		yield put(userActions.IS_UPLOADING_PROFILE_IMAGE(true))
		yield put(userActions.SELF_ERROR_MESSAGE(''))

		const apiRes: ResType = yield call(apiUpdateSelfProfileImage, action.payload.file)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			const user: UserType = yield select(state => state.user.user)
			const data = apiRes.data[0] as any
			yield put(
				userActions.USER(
					new User({ ...user, profileImageUrl: data.profileImageUrl ?? '' }),
				),
			)
		} else {
			// 스태프는 콘텐츠 판정을 거치지 않는다 — 여기서 나오는 거부는 형식·크기·횟수뿐이다
			const status: number = apiRes?.status ?? 0
			yield put(
				userActions.SELF_ERROR_MESSAGE(
					status === 413
						? '파일이 너무 큽니다. 8MB 까지 올릴 수 있습니다.'
						: status === 400
						? '이미지로 읽을 수 없는 파일입니다.'
						: status === 429
						? '오늘 사진을 너무 여러 번 바꿨습니다. 내일 다시 시도해 주세요.'
						: '이미지를 올리지 못했습니다.',
				),
			)
		}

		yield put(userActions.IS_UPLOADING_PROFILE_IMAGE(false))
	} catch (error) {
		Logger.warn(error)
		yield put(userActions.SELF_ERROR_MESSAGE('이미지를 올리지 못했습니다.'))
		yield put(userActions.IS_UPLOADING_PROFILE_IMAGE(false))
	}
}

function* mainSaga() {
	yield takeEvery(userActions.USER, userSaga)
	yield takeEvery(userActions.UPDATE_SELF_PROFILE, updateSelfProfileSaga)
	yield takeEvery(userActions.UPDATE_SELF_PASSWORD, updateSelfPasswordSaga)
	yield takeEvery(userActions.UPDATE_SELF_PROFILE_IMAGE, updateSelfProfileImageSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
